import { lib } from './lib';

const setTruncation = (ctr) => {
	// Add an event listener to the truncated elements.
	const truncatedElms = ctr.querySelectorAll(lib.selectors.truncate);

	truncatedElms.forEach((elm) => {
		// Get the height of the element when the text is not yet truncated.
		const expandedInnerRect = elm.getBoundingClientRect();
		elm.style.setProperty('--truncate-height-expanded', `${expandedInnerRect.height}px`);

		// Add a class to the element to indicate that the text is truncated.
		elm.classList.add(lib.classes.closed, lib.classes.clamp);

		elm.addEventListener('click', () => {
			elm.classList.toggle(lib.classes.closed);

			if (elm.classList.contains(lib.classes.closed)) {
				setTimeout(() => elm.classList.add(lib.classes.clamp), 300);
			} else {
				elm.classList.remove(lib.classes.clamp);
			}
		});
	});
};

const getTheIso8601DateFormat = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
	const day = String(date.getDate()).padStart(2, '0');
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	const timeString = `${year}-${month}-${day}T${hours}:${minutes}`;

	return timeString;
};

const getSchemaAttribute = (itemprop, date = new Date(), inline = true) => {
	const atributes = `itemprop="${itemprop}" content="${getTheIso8601DateFormat(new Date(date))}"`;

	if (inline) {
		return atributes;
	} else {
		return `<span ${atributes}></span>`;
	}
};

const getDates = (startDate, endDate) => {
	const browserLanguage = document.querySelector('html')?.lang;

	// Format the dates and times
	const dateFormatOptions = { year: 'numeric', month: 'long', day: 'numeric', timeZoneName: 'short' };
	const timeFormatOptions = { hour: 'numeric', minute: 'numeric' };

	const dateFormat = new Intl.DateTimeFormat(browserLanguage, dateFormatOptions);
	const timeFormat = new Intl.DateTimeFormat(browserLanguage, timeFormatOptions);

	if (!startDate) {
		return `<div ${getSchemaAttribute('startDate')}"></div>`;
	}

	// Convert PHP timestamps (seconds) to JavaScript timestamps (milliseconds)
	startDate = startDate * 1000;
	endDate = endDate ? endDate * 1000 : null;

	const startDateTime = dateFormat.format(startDate) + ', ' + timeFormat.format(startDate);
	let formattedString = '';
	const schemaStartDate = getSchemaAttribute('startDate', startDate);
	let schemaEndDate = '';

	if (!endDate) {
		formattedString = startDateTime;
	} else if (dateFormat.format(startDate) === dateFormat.format(endDate)) {
		const endTime = timeFormat.format(endDate);
		formattedString = `${startDateTime}&ndash;${endTime}`;
		schemaEndDate = getSchemaAttribute('endDate', endDate, false);
	} else {
		const endDateTime = dateFormat.format(endDate) + ', ' + timeFormat.format(endDate);
		formattedString = `${startDateTime}&ndash;${endDateTime}`;
		schemaEndDate = getSchemaAttribute('endDate', endDate, false);
	}

	return `<div class="${lib.classes.date}"><span class="${lib.classes.icon}">event</span><span ${schemaStartDate}>${formattedString}</span>${schemaEndDate}</div>`;
};

const getAddress = (city) => {
	return `
	<div itemprop="address" itemscope itemtype="https://schema.org/PostalAddress">
		, <span itemprop="addressLocality">${city}</span>
	</div>
	`;
};

const getBadges = (date) => {
	let badges = '';

	date.categories.forEach((category) => {
		badges += `<span class="${lib.classes.badge}">${category}</span>`;
	});

	return `<div class="${lib.classes.badges}">${badges}</div>`;
};

const getLocation = (location, online) => {
	const name = `
		<span class="${lib.classes.icon}">location_on</span>
		<span itemprop="name">${location.online ? online : location.name}</span>
		${!location.online ? getAddress(location.city) : ''}
	`;

	return `
	<div itemprop="location" itemscope itemtype="https://schema.org/Place" class="${lib.classes.location}">
		${location.maps_url !== '' ? `<a href="${location.maps_url}" target="_blank" rel="noopener">${name}</a>` : name}
	</div>
	`;
};

const createAnchorTag = (link) => {
	return `<a href="${link.url}" target="_blank" class="btn btn-secondary ${link.indicator}" itemprop="url">${link.label}</a>`;
};

const getTheLink = (links, dates) => {
	if (dates.end_date === null || dates.start_date === null) {
		return links.always.url !== '' ? createAnchorTag(links.always) : '';
	}

	const currentTimestamp = Math.floor(Date.now() / 1000);

	if (dates.end_date > currentTimestamp) {
		// Check if the event is still in the future, then display a link that is relevant to the future of the event.
		return links.before.url !== '' ? createAnchorTag(links.before) : '';
	} else {
		// Check if the event is in the past, then display a link that is relevant to the past of the event.
		return links.after.url !== '' ? createAnchorTag(links.after) : '';
	}
};

const getDateStructure = (date, labels) => {
	const { title, thumb, links, dates, excerpt, location } = date;

	return `
	<div class="row event" itemscope itemtype="https://schema.org/Event">
		<div class="col-xl-5">${thumb}</div>
		<div class="col-xl-7">
			${getBadges(date)}
			<h5 itemprop="name">${title}</h5>
			<div class="${lib.classes.meta}">
				${getDates(dates.start_date, dates.end_date, dates.time_zone)}
				${location && dates.end_date !== null ? getLocation(location, labels.online) : ''}
			</div>
			<div itemprop="description" class="${lib.classes.description} ${excerpt.length > 70 && lib.classes.truncate}">
				<div class="${lib.classes.descriptionInner}">${excerpt.text}</div>
			</div>
			${getTheLink(links, dates)}
		</div>
	</div>`;
};

const renderEvents = (node, labels, events) => {
	// Create wrapper for each event section.
	const wrapper = document.createElement('div');
	wrapper.setAttribute('class', lib.classes.ctr);

	events.forEach((date) => {
		wrapper.innerHTML += getDateStructure(date, labels);
	});

	node.append(wrapper);
};

const renderNoEvents = (ctr, message) => {
	const noEvents = document.createElement('div');
	noEvents.setAttribute('class', lib.classes.noEvents);
	const paragraph = document.createElement('p');
	paragraph.setAttribute('class', lib.classes.infobox);
	paragraph.innerText = message;

	noEvents.append(paragraph);
	ctr.append(noEvents);
};

export { getAddress, getBadges, getDateStructure, renderEvents, renderNoEvents, setTruncation };
